/**
 * React Query hooks for the FTL API.
 *
 * Query hooks fetch data and cache it with sensible staleTime / query keys.
 * Mutation hooks perform writes and automatically invalidate related queries
 * on success so the UI stays in sync without manual refetching.
 *
 * The QueryClient in App.tsx already sets a global staleTime of 30 s.
 * Individual hooks can override this where it makes sense.
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryOptions,
  type UseMutationOptions,
} from "@tanstack/react-query";
import { useMemo } from "react";

import { deriveProjectFacts } from "@/lib/projectFacts";
import { ApiRequestError, setAccessToken } from "./client";
import {
  authApi,
  gitConfigApi,
  projectsApi,
  modulesApi,
  componentsApi,
  componentSpecificationsApi,
  objectivesApi,
  tasksApi,
  testCasesApi,
  testExecutionsApi,
  defectsApi,
  documentsApi,
  changeRequestsApi,
  changeRequestScopeApi,
  baselinesApi,
  auditApi,
  usersApi,
  rolesApi,
  statusConfigApi,
  settingsApi,
  notificationsApi,
  metricsApi,
  personasApi,
  healthApi,
  workflowsApi,
  reportsApi,
  billingApi,
  qaSessionsApi,
  provenanceApi,
  traceabilityLinksApi,
  conflictsApi,
  userProfilesApi,
  meetingsApi,
  sprintsApi,
  importsApi,
  documentPlansApi,
  documentExportApi,
  demoFeedbackApi,
  adIntegrationApi,
  userStoriesApi,
  userPersonasApi,
  outcomesApi,
  outcomeRecordApi,
  assistantApi,
  agentLoopApi,
  analyticsApi,
  favoritesApi,
  dashboardApi,
  platformDevAnalyticsApi,
  platformAnalyticsApi,
  qaActionItemsApi,
  infrastructureApi,
  calendarApi,
} from "./services";
import type { QASessionListParams } from "./services";

import type {
  OutcomeCriterionEvidence,
  GitConfigUpdate,
  Baseline,
  ChangeRequest,
  ScopeViolationRequest,
  Component,
  ComponentSpecification,
  Defect,
  Document,
  DocumentTemplate,
  GenerateDocumentRequest,
  RegenerateDocumentRequest,
  HealthAssessment,
  LineageMap,
  ListParams,
  Metric,
  Notification,
  NotificationListParams,
  Objective,
  PaginatedResponse,
  Persona,
  Plan,
  Project,
  ProvenanceResult,
  QASession,
  QASessionDetail,
  Report,
  Role,
  Setting,
  StatusChangeRequest,
  StatusChangeResult,
  StatusConfig,
  Subscription,
  Task,
  TestCase,
  TestExecution,
  User,
  WorkflowDefinition,
  WorkflowRun,
  WorkflowRunListParams,
  EntityConflict,
  UserProfile,
  Meeting,
  MeetingActionItem,
  MeetingMinutes,
  Sprint,
  SprintItem,
  Milestone,
  BurndownSnapshot,
  RequirementImport,
  ImportRequirementItem,
  DocumentTypeInfo,
  ProjectDocumentPlan,
} from "./types";

// ---------------------------------------------------------------------------
// Query key factory -- keeps keys consistent across hooks & invalidations
// ---------------------------------------------------------------------------

export const queryKeys = {
  // Auth
  me: ["auth", "me"] as const,

  // Core entities
  projects: {
    all: ["projects"] as const,
    list: (params?: ListParams) => ["projects", "list", params] as const,
    detail: (id: string) => ["projects", "detail", id] as const,
    history: (id: string) => ["projects", "history", id] as const,
  },
  modules: {
    all: ["modules"] as const,
    list: (params?: ListParams) => ["modules", "list", params] as const,
    detail: (id: string) => ["modules", "detail", id] as const,
    history: (id: string) => ["modules", "history", id] as const,
  },
  components: {
    all: ["components"] as const,
    list: (params?: ListParams) => ["components", "list", params] as const,
    detail: (id: string) => ["components", "detail", id] as const,
    history: (id: string) => ["components", "history", id] as const,
  },
  componentSpecifications: {
    all: ["componentSpecifications"] as const,
    list: (params?: ListParams) => ["componentSpecifications", "list", params] as const,
    detail: (id: string) => ["componentSpecifications", "detail", id] as const,
    history: (id: string) => ["componentSpecifications", "history", id] as const,
  },
  objectives: {
    all: ["objectives"] as const,
    list: (params?: ListParams) => ["objectives", "list", params] as const,
    detail: (id: string) => ["objectives", "detail", id] as const,
    outcomes: (id: string) => ["objectives", "outcomes", id] as const,
    history: (id: string) => ["objectives", "history", id] as const,
  },
  tasks: {
    all: ["tasks"] as const,
    list: (params?: ListParams) => ["tasks", "list", params] as const,
    detail: (id: string) => ["tasks", "detail", id] as const,
    history: (id: string) => ["tasks", "history", id] as const,
  },
  testCases: {
    all: ["testCases"] as const,
    list: (params?: ListParams) => ["testCases", "list", params] as const,
    detail: (id: string) => ["testCases", "detail", id] as const,
    history: (id: string) => ["testCases", "history", id] as const,
  },
  testExecutions: {
    all: ["testExecutions"] as const,
    list: (params?: ListParams) => ["testExecutions", "list", params] as const,
    detail: (id: string) => ["testExecutions", "detail", id] as const,
  },
  defects: {
    all: ["defects"] as const,
    list: (params?: ListParams) => ["defects", "list", params] as const,
    detail: (id: string) => ["defects", "detail", id] as const,
    history: (id: string) => ["defects", "history", id] as const,
  },
  documents: {
    all: ["documents"] as const,
    list: (params?: ListParams) => ["documents", "list", params] as const,
    detail: (id: string) => ["documents", "detail", id] as const,
    history: (id: string) => ["documents", "history", id] as const,
    templates: ["documents", "templates"] as const,
    versions: (id: string) => ["documents", "versions", id] as const,
    lifetimeCost: (id: string) => ["documents", "lifetimeCost", id] as const,
  },
  changeRequests: {
    all: ["changeRequests"] as const,
    list: (params?: ListParams) => ["changeRequests", "list", params] as const,
    detail: (id: string) => ["changeRequests", "detail", id] as const,
    history: (id: string) => ["changeRequests", "history", id] as const,
    impactTree: (ids: string[]) => ["changeRequests", "impactTree", [...ids].sort().join(",")] as const,
  },
  baselines: {
    all: ["baselines"] as const,
    list: (params?: ListParams) => ["baselines", "list", params] as const,
    detail: (id: string) => ["baselines", "detail", id] as const,
    history: (id: string) => ["baselines", "history", id] as const,
  },

  // Cross-cutting concerns
  audit: {
    timeline: (entityType: string, entityId: string) =>
      ["audit", "timeline", entityType, entityId] as const,
    project: (projectId: string) => ["audit", "project", projectId] as const,
  },
  users: {
    all: ["users"] as const,
    list: (params?: ListParams) => ["users", "list", params] as const,
    detail: (id: string) => ["users", "detail", id] as const,
  },
  roles: ["roles"] as const,
  statusConfig: ["statusConfig"] as const,
  settings: ["settings"] as const,
  notifications: {
    all: ["notifications"] as const,
    list: (params?: NotificationListParams) =>
      ["notifications", "list", params] as const,
  },
  metrics: {
    all: ["metrics"] as const,
    list: (params?: ListParams) => ["metrics", "list", params] as const,
    detail: (id: string) => ["metrics", "detail", id] as const,
  },
  personas: ["personas"] as const,
  health: {
    latest: (projectId?: string) => ["health", "latest", projectId] as const,
  },
  workflows: {
    registry: ["workflows", "registry"] as const,
    runs: (params?: WorkflowRunListParams) =>
      ["workflows", "runs", params] as const,
    run: (runId: string) => ["workflows", "run", runId] as const,
  },
  reports: {
    all: ["reports"] as const,
    list: (params?: ListParams) => ["reports", "list", params] as const,
  },
  billing: {
    subscription: ["billing", "subscription"] as const,
    plans: ["billing", "plans"] as const,
    usage: (resource?: string) => ["billing", "usage", resource] as const,
  },
  qaSessions: {
    all: ["qaSessions"] as const,
    list: (params?: QASessionListParams) => ["qaSessions", "list", params] as const,
    detail: (id: string) => ["qaSessions", "detail", id] as const,
    conflicts: (id: string) => ["qaSessions", "conflicts", id] as const,
    impact: (id: string) => ["qaSessions", "impact", id] as const,
  },
  provenance: {
    detail: (entityType: string, entityId: string) =>
      ["provenance", entityType, entityId] as const,
    lineageMap: (entityType: string, entityId: string) =>
      ["provenance", "lineageMap", entityType, entityId] as const,
  },
  traceabilityLinks: {
    detail: (entityType: string, entityId: string) =>
      ["traceabilityLinks", entityType, entityId] as const,
    graph: (entityType: string, entityId: string) =>
      ["traceabilityLinks", "graph", entityType, entityId] as const,
  },

  // New feature areas
  conflicts: {
    all: ["conflicts"] as const,
    forProject: (projectId: string) => ["conflicts", "project", projectId] as const,
    detail: (id: string) => ["conflicts", "detail", id] as const,
    forEntity: (entityType: string, entityId: string) => ["conflicts", "entity", entityType, entityId] as const,
  },
  userProfiles: {
    me: ["userProfiles", "me"] as const,
    detail: (userId: string) => ["userProfiles", "detail", userId] as const,
    team: ["userProfiles", "team"] as const,
  },
  meetings: {
    all: ["meetings"] as const,
    forProject: (projectId: string) => ["meetings", "project", projectId] as const,
    detail: (id: string) => ["meetings", "detail", id] as const,
    transcript: (id: string) => ["meetings", "transcript", id] as const,
    minutes: (id: string) => ["meetings", "minutes", id] as const,
    actionItems: (id: string) => ["meetings", "actionItems", id] as const,
    byDateRange: (start: string, end: string, projectId?: string) =>
      ["meetings", "dateRange", start, end, projectId ?? "all"] as const,
  },
  sprints: {
    all: ["sprints"] as const,
    forProject: (projectId: string) => ["sprints", "project", projectId] as const,
    detail: (id: string) => ["sprints", "detail", id] as const,
    backlog: (id: string) => ["sprints", "backlog", id] as const,
    burndown: (id: string) => ["sprints", "burndown", id] as const,
    velocity: (projectId: string) => ["sprints", "velocity", projectId] as const,
    milestones: (projectId: string) => ["sprints", "milestones", projectId] as const,
  },
  imports: {
    all: ["imports"] as const,
    detail: (id: string) => ["imports", "detail", id] as const,
    items: (id: string) => ["imports", "items", id] as const,
    gaps: (id: string) => ["imports", "gaps", id] as const,
    conflicts: (id: string) => ["imports", "conflicts", id] as const,
  },
  documentPlans: {
    all: ["documentPlans"] as const,
    types: ["documentPlans", "types"] as const,
    forProject: (projectId: string) => ["documentPlans", "project", projectId] as const,
  },

  // Document Export
  documentExport: {
    html: (documentId: string) => ["documentExport", "html", documentId] as const,
    pdf: (documentId: string) => ["documentExport", "pdf", documentId] as const,
    docx: (documentId: string) => ["documentExport", "docx", documentId] as const,
  },

  // Demo Feedback
  demoFeedback: {
    all: ["demoFeedback"] as const,
    candidates: (meetingId: string) => ["demoFeedback", "candidates", meetingId] as const,
    feedback: (meetingId: string) => ["demoFeedback", "feedback", meetingId] as const,
  },

  // AD Integration
  adIntegration: {
    all: ["adIntegration"] as const,
    status: ["adIntegration", "status"] as const,
    users: ["adIntegration", "users"] as const,
    groups: ["adIntegration", "groups"] as const,
  },

  // User Personas
  userPersonas: {
    all: ["userPersonas"] as const,
    list: (params?: ListParams) => ["userPersonas", "list", params] as const,
    detail: (id: string) => ["userPersonas", "detail", id] as const,
    history: (id: string) => ["userPersonas", "history", id] as const,
    stories: (id: string) => ["userPersonas", "stories", id] as const,
  },

  // User Stories
  userStories: {
    all: ["userStories"] as const,
    list: (params?: ListParams) => ["userStories", "list", params] as const,
    detail: (id: string) => ["userStories", "detail", id] as const,
    history: (id: string) => ["userStories", "history", id] as const,
  },

  // Outcomes
  outcomes: {
    all: ["outcomes"] as const,
    list: (params?: ListParams) => ["outcomes", "list", params] as const,
    detail: (id: string) => ["outcomes", "detail", id] as const,
    history: (id: string) => ["outcomes", "history", id] as const,
  },
} as const;

// ---------------------------------------------------------------------------
// Auth hooks
// ---------------------------------------------------------------------------

export function useCurrentUser(
  options?: Partial<UseQueryOptions<ReturnType<typeof authApi.me> extends Promise<infer R> ? R : never>>,
) {
  return useQuery({
    queryKey: queryKeys.me,
    queryFn: () => authApi.me(),
    staleTime: 5 * 60_000, // 5 min -- user rarely changes
    ...options,
  });
}

// ---------------------------------------------------------------------------
// Projects
// ---------------------------------------------------------------------------

export function useProjects(params?: ListParams) {
  return useQuery({
    queryKey: queryKeys.projects.list(params),
    queryFn: () => projectsApi.list(params),
  });
}

export function useFocus(projectId: string | undefined) {
  return useQuery({
    queryKey: ["focus", projectId],
    queryFn: () => projectsApi.focus(projectId!),
    enabled: !!projectId,
    staleTime: 30_000, // 30s — focus data doesn't change rapidly
  });
}

export function useWorkflowProgress(projectId: string | undefined) {
  return useQuery({
    queryKey: ["workflowProgress", projectId],
    queryFn: () => projectsApi.workflowProgress(projectId!),
    enabled: !!projectId,
    staleTime: 30_000,
  });
}

/** Outcome Record verdict (OUT-03/PR-14) — the "are we done?" read. */
export function useOutcomeRecordVerdict(projectId: string | undefined) {
  return useQuery({
    queryKey: ["outcomeRecord", "verdict", projectId],
    queryFn: () => outcomeRecordApi.getVerdict(projectId!),
    enabled: !!projectId,
    staleTime: 30_000,
  });
}

/** Score the outcome's criteria. OUT-03.
 *
 *  Invalidates the verdict so the card re-reads its own result rather than
 *  trusting the optimistic shape of the response -- the server applies the
 *  WF006 precedence (explicit `met` -> numeric ratio -> UNMEASURED) and the
 *  displayed verdict must be the one it actually stored.
 */
export function useScoreOutcomeRecord(projectId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (evidence: Record<string, OutcomeCriterionEvidence>) =>
      outcomeRecordApi.score(projectId!, evidence),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["outcomeRecord", "verdict", projectId] });
    },
  });
}

export function useOrgSummary() {
  return useQuery({
    queryKey: ["orgSummary"],
    queryFn: () => analyticsApi.getOrgSummary(),
    staleTime: 60_000, // 60s — org-level data refreshes less often
  });
}

export function useQualityMetrics(projectId: string | undefined) {
  return useQuery({
    queryKey: ["analytics", "quality", projectId],
    queryFn: () => analyticsApi.getQualityMetrics(projectId!),
    enabled: !!projectId,
    staleTime: 30_000,
  });
}

export function useVelocity(projectId: string | undefined, days?: number) {
  return useQuery({
    queryKey: ["analytics", "velocity", projectId, days],
    queryFn: () => analyticsApi.getVelocity(projectId!, days),
    enabled: !!projectId,
    staleTime: 30_000,
  });
}

export function useCoverageMatrix(projectId: string | undefined) {
  return useQuery({
    queryKey: ["analytics", "coverage", projectId],
    queryFn: () => analyticsApi.getCoverageMatrix(projectId!),
    enabled: !!projectId,
    staleTime: 30_000,
  });
}

export function useTeamContributions(projectId: string | undefined, days?: number) {
  return useQuery({
    queryKey: ["analytics", "team", projectId, days],
    queryFn: () => analyticsApi.getTeamContributions(projectId!, days),
    enabled: !!projectId,
    staleTime: 30_000,
  });
}

export function useRiskMatrix(projectId: string | undefined) {
  return useQuery({
    queryKey: ["analytics", "risk", projectId],
    queryFn: () => analyticsApi.getRiskMatrix(projectId!),
    enabled: !!projectId,
    staleTime: 30_000,
  });
}

export function useProjectAICosts(projectId: string | undefined) {
  return useQuery({
    queryKey: ["analytics", "ai-costs", projectId],
    queryFn: () => analyticsApi.getProjectAICosts(projectId!),
    enabled: !!projectId,
    staleTime: 30_000,
  });
}

export function useOrgAICosts() {
  return useQuery({
    queryKey: ["analytics", "org-ai-costs"],
    queryFn: () => analyticsApi.getOrgAICosts(),
    staleTime: 60_000,
  });
}

export function useOrgAICostsTrend(
  bucket: "hour" | "day" | "week" | "month" = "day",
  days: number = 30,
) {
  return useQuery({
    queryKey: ["analytics", "org-ai-costs-trend", bucket, days],
    queryFn: () => analyticsApi.getOrgAICostsTrend(bucket, days),
    staleTime: 60_000,
  });
}

/** Authoritative Anthropic cost — 5 min cache since the upstream API
 *  freshness isn't documented but is close to real-time. */
export function useOrgAICostsAuthoritative(days: number = 30) {
  return useQuery({
    queryKey: ["analytics", "org-ai-costs-authoritative", days],
    queryFn: () => analyticsApi.getOrgAICostsAuthoritative(days),
    staleTime: 5 * 60_000,
    retry: 1,
  });
}

export function useEntityAICost(entityType: string | undefined, entityId: string | undefined) {
  return useQuery({
    queryKey: ["analytics", "entity-ai-cost", entityType, entityId],
    queryFn: () => analyticsApi.getEntityAICost(entityType!, entityId!),
    enabled: !!entityType && !!entityId,
    staleTime: 30_000,
  });
}

export function useDevelopmentTimeline(projectId: string | undefined) {
  return useQuery({
    queryKey: ["analytics", "development-timeline", projectId],
    queryFn: () => analyticsApi.getDevelopmentTimeline(projectId!),
    enabled: !!projectId,
    staleTime: 60_000,
  });
}

export function useCostBreakdown(projectId: string | undefined, bucket?: string, days?: number) {
  return useQuery({
    queryKey: ["analytics", "cost-breakdown", projectId, bucket, days],
    queryFn: () => analyticsApi.getCostBreakdown(projectId!, bucket, days),
    enabled: !!projectId,
    staleTime: 60_000,
  });
}

export function useOrgDevelopmentAnalytics(days?: number) {
  return useQuery({
    queryKey: ["analytics", "org-development", days],
    queryFn: () => analyticsApi.getOrgDevelopmentAnalytics(days),
    staleTime: 60_000,
  });
}

export function useOrgQualityMetrics() {
  return useQuery({
    queryKey: ["analytics", "org-quality-metrics"],
    queryFn: () => analyticsApi.getOrgQualityMetrics(),
    staleTime: 60_000,
  });
}

export function useOrgTeamContributions(days?: number) {
  return useQuery({
    queryKey: ["analytics", "org-team-contributions", days],
    queryFn: () => analyticsApi.getOrgTeamContributions(days),
    staleTime: 60_000,
  });
}

export function useOrgFinancialSummary(days?: number) {
  return useQuery({
    queryKey: ["analytics", "org-financial-summary", days],
    queryFn: () => analyticsApi.getOrgFinancialSummary(days),
    staleTime: 60_000,
  });
}

export function usePlatformDevelopmentAnalytics(days?: number) {
  return useQuery({
    queryKey: ["platform", "development-analytics", days],
    queryFn: () => platformDevAnalyticsApi.get(days),
    staleTime: 60_000,
  });
}

export function usePlatformOverview() {
  return useQuery({
    queryKey: ["platform", "analytics-overview"],
    queryFn: () => platformAnalyticsApi.getOverview(),
    staleTime: 60_000,
  });
}

export function usePlatformFinancialTrends(days?: number) {
  return useQuery({
    queryKey: ["platform", "financial-trends", days],
    queryFn: () => platformAnalyticsApi.getFinancialTrends(days),
    staleTime: 60_000,
  });
}

export function usePlatformOrgComparison(days?: number) {
  return useQuery({
    queryKey: ["platform", "org-comparison", days],
    queryFn: () => platformAnalyticsApi.getOrgComparison(days),
    staleTime: 60_000,
  });
}

export function usePlatformUserAnalytics(days?: number) {
  return useQuery({
    queryKey: ["platform", "user-analytics", days],
    queryFn: () => platformAnalyticsApi.getUserAnalytics(days),
    staleTime: 60_000,
  });
}

export function useProjectSummary(projectId: string | undefined) {
  return useQuery({
    queryKey: ["analytics", "summary", projectId],
    queryFn: () => analyticsApi.getProjectSummary(projectId!),
    enabled: !!projectId,
    staleTime: 30_000,
  });
}

/** Every project fact (requirements approved/done, tasks, tests, workstreams,
 *  milestones) from the one summary response, named through projectFacts.ts.
 *  `facts` is null until the summary arrives — never a fabricated 0. */
export function useProjectFacts(projectId: string | undefined) {
  const q = useProjectSummary(projectId);
  const facts = useMemo(() => (q.data ? deriveProjectFacts(q.data) : null), [q.data]);
  return { facts, isLoading: q.isLoading, isError: q.isError };
}

export function useActivityTrend(projectId: string | undefined, days?: number) {
  return useQuery({
    queryKey: ["analytics", "trend", projectId, days],
    queryFn: () => analyticsApi.getActivityTrend(projectId!, days),
    enabled: !!projectId,
    staleTime: 30_000,
  });
}

export function useProject(id: string) {
  return useQuery({
    queryKey: queryKeys.projects.detail(id),
    queryFn: () => projectsApi.get(id),
    enabled: !!id,
    // A 404 is an answer, not a blip (row 2b6d6836: a project in another org).
    // Retrying it only delays the project page's "switch org?" prompt.
    retry: (count, err) => (err as { status?: number })?.status !== 404 && count < 1,
  });
}

export function useCreateProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<Project>) => projectsApi.create(body),
    onSuccess: (data) => {
      // If the backend granted a new role, it returns a fresh access token.
      // Store it so subsequent requests use the updated roles immediately.
      if (data && typeof data === "object" && "access_token" in data) {
        setAccessToken((data as Record<string, unknown>).access_token as string);
      }
      qc.invalidateQueries({ queryKey: queryKeys.projects.all });
    },
  });
}

export function useUpdateProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: Partial<Project> & { id: string }) =>
      projectsApi.update(id, body),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: queryKeys.projects.all });
      qc.invalidateQueries({ queryKey: queryKeys.projects.detail(vars.id) });
    },
  });
}

export function useDeleteProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => projectsApi.delete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.projects.all });
    },
  });
}

// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// Objectives — QA
// ---------------------------------------------------------------------------

export function useStartObjectiveQA() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => objectivesApi.startQA(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.workflows.registry });
      qc.invalidateQueries({ queryKey: queryKeys.qaSessions.all });
    },
  });
}


// ---------------------------------------------------------------------------
// Components
// ---------------------------------------------------------------------------

export function useComponents(params?: ListParams) {
  return useQuery({
    queryKey: queryKeys.components.list(params),
    queryFn: () => componentsApi.list(params),
  });
}

export function useComponent(id: string) {
  return useQuery({
    queryKey: queryKeys.components.detail(id),
    queryFn: () => componentsApi.get(id),
    enabled: !!id,
  });
}

export function useCreateComponent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<Component>) => componentsApi.create(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.components.all });
    },
  });
}

export function useUpdateComponent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: Partial<Component> & { id: string }) =>
      componentsApi.update(id, body),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: queryKeys.components.all });
      qc.invalidateQueries({ queryKey: queryKeys.components.detail(vars.id) });
    },
  });
}

export function useDeleteComponent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => componentsApi.delete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.components.all });
    },
  });
}

// ---------------------------------------------------------------------------
// Component Specifications
// ---------------------------------------------------------------------------

export function useComponentSpecifications(params?: ListParams & { component_id?: string; spec_type?: string }) {
  return useQuery({
    queryKey: queryKeys.componentSpecifications.list(params),
    queryFn: () => componentSpecificationsApi.list(params),
  });
}

export function useComponentSpecification(id: string) {
  return useQuery({
    queryKey: queryKeys.componentSpecifications.detail(id),
    queryFn: () => componentSpecificationsApi.get(id),
    enabled: !!id,
  });
}

export function useCreateComponentSpecification() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<ComponentSpecification>) => componentSpecificationsApi.create(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.componentSpecifications.all });
    },
  });
}

export function useUpdateComponentSpecification() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: Partial<ComponentSpecification> & { id: string }) =>
      componentSpecificationsApi.update(id, body),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: queryKeys.componentSpecifications.all });
      qc.invalidateQueries({ queryKey: queryKeys.componentSpecifications.detail(vars.id) });
    },
  });
}

export function useDeleteComponentSpecification() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => componentSpecificationsApi.delete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.componentSpecifications.all });
    },
  });
}

// ---------------------------------------------------------------------------
// Objectives
// ---------------------------------------------------------------------------

export function useObjectives(params?: ListParams) {
  return useQuery({
    queryKey: queryKeys.objectives.list(params),
    queryFn: () => objectivesApi.list(params),
  });
}

export function useObjective(id: string) {
  return useQuery({
    queryKey: queryKeys.objectives.detail(id),
    queryFn: () => objectivesApi.get(id),
    enabled: !!id,
  });
}

export function useObjectiveOutcomes(id: string) {
  return useQuery({
    queryKey: queryKeys.objectives.outcomes(id),
    queryFn: () => objectivesApi.getOutcomes(id),
    enabled: !!id,
  });
}

export function useCreateObjective() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<Objective>) => objectivesApi.create(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.objectives.all });
    },
  });
}

export function useUpdateObjective() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: Partial<Objective> & { id: string }) =>
      objectivesApi.update(id, body),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: queryKeys.objectives.all });
      qc.invalidateQueries({ queryKey: queryKeys.objectives.detail(vars.id) });
    },
  });
}

export function useDeleteObjective() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => objectivesApi.delete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.objectives.all });
    },
  });
}

// ---------------------------------------------------------------------------
// Tasks
// ---------------------------------------------------------------------------

export function useTasks(params?: ListParams) {
  return useQuery({
    queryKey: queryKeys.tasks.list(params),
    queryFn: () => tasksApi.list(params),
  });
}

export function useTask(id: string) {
  return useQuery({
    queryKey: queryKeys.tasks.detail(id),
    queryFn: () => tasksApi.get(id),
    enabled: !!id,
  });
}

export function useCreateTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<Task>) => tasksApi.create(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.tasks.all });
    },
  });
}

export function useUpdateTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: Partial<Task> & { id: string }) =>
      tasksApi.update(id, body),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: queryKeys.tasks.all });
      qc.invalidateQueries({ queryKey: queryKeys.tasks.detail(vars.id) });
    },
  });
}

export function useDeleteTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => tasksApi.delete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.tasks.all });
    },
  });
}

// ---------------------------------------------------------------------------
// Test Cases
// ---------------------------------------------------------------------------

export function useTestCases(params?: ListParams) {
  return useQuery({
    queryKey: queryKeys.testCases.list(params),
    queryFn: () => testCasesApi.list(params),
  });
}

export function useTestCase(id: string) {
  return useQuery({
    queryKey: queryKeys.testCases.detail(id),
    queryFn: () => testCasesApi.get(id),
    enabled: !!id,
  });
}

export function useCreateTestCase() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<TestCase>) => testCasesApi.create(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.testCases.all });
    },
  });
}

export function useUpdateTestCase() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: Partial<TestCase> & { id: string }) =>
      testCasesApi.update(id, body),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: queryKeys.testCases.all });
      qc.invalidateQueries({ queryKey: queryKeys.testCases.detail(vars.id) });
    },
  });
}

export function useDeleteTestCase() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => testCasesApi.delete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.testCases.all });
    },
  });
}

// ---------------------------------------------------------------------------
// Test Executions
// ---------------------------------------------------------------------------

export function useTestExecutions(params?: ListParams) {
  return useQuery({
    queryKey: queryKeys.testExecutions.list(params),
    queryFn: () => testExecutionsApi.list(params),
  });
}

export function useTestExecution(id: string) {
  return useQuery({
    queryKey: queryKeys.testExecutions.detail(id),
    queryFn: () => testExecutionsApi.get(id),
    enabled: !!id,
  });
}

export function useCreateTestExecution() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<TestExecution>) =>
      testExecutionsApi.create(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.testExecutions.all });
      // Also invalidate test cases since execution results affect them
      qc.invalidateQueries({ queryKey: queryKeys.testCases.all });
    },
  });
}

// ---------------------------------------------------------------------------
// Defects
// ---------------------------------------------------------------------------

export function useDefects(params?: ListParams) {
  return useQuery({
    queryKey: queryKeys.defects.list(params),
    queryFn: () => defectsApi.list(params),
  });
}

export function useDefect(id: string) {
  return useQuery({
    queryKey: queryKeys.defects.detail(id),
    queryFn: () => defectsApi.get(id),
    enabled: !!id,
  });
}

export function useCreateDefect() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<Defect>) => defectsApi.create(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.defects.all });
    },
  });
}

export function useUpdateDefect() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: Partial<Defect> & { id: string }) =>
      defectsApi.update(id, body),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: queryKeys.defects.all });
      qc.invalidateQueries({ queryKey: queryKeys.defects.detail(vars.id) });
    },
  });
}

export function useDeleteDefect() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => defectsApi.delete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.defects.all });
    },
  });
}

// ---------------------------------------------------------------------------
// Documents
// ---------------------------------------------------------------------------

export function useDocuments(params?: ListParams & { scope?: "project" | "org" }) {
  return useQuery({
    queryKey: queryKeys.documents.list(params),
    queryFn: () => documentsApi.list(params),
  });
}

export function useDocument(id: string) {
  return useQuery({
    queryKey: queryKeys.documents.detail(id),
    queryFn: () => documentsApi.get(id),
    enabled: !!id,
  });
}

export function useCreateDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<Document>) => documentsApi.create(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.documents.all });
    },
  });
}

export function useUpdateDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: Partial<Document> & { id: string }) =>
      documentsApi.update(id, body),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: queryKeys.documents.all });
      qc.invalidateQueries({ queryKey: queryKeys.documents.detail(vars.id) });
    },
  });
}

export function useDeleteDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => documentsApi.delete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.documents.all });
    },
  });
}

export function useSubmitDocumentReview() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => documentsApi.submitReview(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.documents.all });
    },
  });
}

export function useDocumentTemplates(scope?: "project" | "org") {
  return useQuery({
    queryKey: [...queryKeys.documents.templates, scope],
    queryFn: () => documentsApi.listTemplates(scope),
    staleTime: 5 * 60 * 1000, // Templates rarely change
  });
}

export function useDocumentAudiences() {
  return useQuery({
    queryKey: ["documents", "audiences"],
    queryFn: () => documentsApi.listAudiences(),
    // The org's role set changes rarely, and a stale picker would offer a role
    // the writer then refuses — so this is cached but not forever.
    staleTime: 5 * 60 * 1000,
  });
}

export function useGenerateDocument() {
  return useMutation({
    mutationFn: (body: GenerateDocumentRequest) => documentsApi.generate(body),
    // No cache invalidation here — the document list refreshes when the user
    // navigates back to the Documents page or when Felix push notification
    // triggers a manual refetch.
  });
}

export function useDocumentVersions(docId: string) {
  return useQuery({
    queryKey: queryKeys.documents.versions(docId),
    queryFn: () => documentsApi.listVersions(docId),
    enabled: !!docId,
  });
}

export function useRegenerateDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string } & RegenerateDocumentRequest) =>
      documentsApi.regenerate(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.documents.all });
    },
  });
}

export function useDocumentLifetimeCost(docId: string) {
  return useQuery({
    queryKey: queryKeys.documents.lifetimeCost(docId),
    queryFn: () => documentsApi.getLifetimeCost(docId),
    enabled: !!docId,
  });
}

// ---------------------------------------------------------------------------
// Change Requests
// ---------------------------------------------------------------------------

export function useChangeRequests(params?: ListParams) {
  return useQuery({
    queryKey: queryKeys.changeRequests.list(params),
    queryFn: () => changeRequestsApi.list(params),
  });
}

export function useChangeRequest(id: string) {
  return useQuery({
    queryKey: queryKeys.changeRequests.detail(id),
    queryFn: () => changeRequestsApi.get(id),
    enabled: !!id,
  });
}

/** Row aabced55: the Impact Tree. A 404 means BLAST_TREE is off: no retry, and
 * the caller renders nothing. */
export function useImpactTree(ids: string[]) {
  return useQuery({
    queryKey: queryKeys.changeRequests.impactTree(ids),
    queryFn: () => changeRequestsApi.impactTree(ids),
    enabled: ids.length > 0,
    retry: (count, err) =>
      !(err instanceof ApiRequestError && err.status === 404) && count < 2,
  });
}

export function useCreateChangeRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<ChangeRequest>) =>
      changeRequestsApi.create(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.changeRequests.all });
    },
  });
}

/**
 * Create a change request from a baseline scope violation. The backend runs
 * impact analysis and stamps scope_violation_source — use this (not the
 * generic create) for the scope-aware baseline gate's "create CR" flow.
 */
export function useCreateChangeRequestFromScopeViolation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ScopeViolationRequest) =>
      changeRequestScopeApi.fromScopeViolation(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.changeRequests.all });
    },
  });
}

export function useUpdateChangeRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: Partial<ChangeRequest> & { id: string }) =>
      changeRequestsApi.update(id, body),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: queryKeys.changeRequests.all });
      qc.invalidateQueries({
        queryKey: queryKeys.changeRequests.detail(vars.id),
      });
    },
  });
}

export function useDeleteChangeRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => changeRequestsApi.delete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.changeRequests.all });
    },
  });
}

/** Dedicated approve hook — calls POST /change-requests/:id/approve
 *  (the specialized endpoint) instead of the generic /status transition.
 *  This is what actually unlocks the target entity and flips status to
 *  APPROVED atomically; the generic status endpoint doesn't handle the
 *  entity unlock side effect. Fixes "can't approve a change request". */
export function useApproveChangeRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      approval_notes,
      unlock_entity = true,
    }: {
      id: string;
      approval_notes?: string;
      unlock_entity?: boolean;
    }) => changeRequestScopeApi.approve(id, { approval_notes, unlock_entity }),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: queryKeys.changeRequests.all });
      qc.invalidateQueries({
        queryKey: queryKeys.changeRequests.detail(vars.id),
      });
    },
  });
}

// ---------------------------------------------------------------------------
// Baselines
// ---------------------------------------------------------------------------

export function useBaselines(params?: ListParams) {
  return useQuery({
    queryKey: queryKeys.baselines.list(params),
    queryFn: () => baselinesApi.list(params),
  });
}

export function useBaseline(id: string) {
  return useQuery({
    queryKey: queryKeys.baselines.detail(id),
    queryFn: () => baselinesApi.get(id),
    enabled: !!id,
  });
}

export function useCreateBaseline() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<Baseline>) => baselinesApi.create(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.baselines.all });
    },
  });
}

export function useUpdateBaseline() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: Partial<Baseline> & { id: string }) =>
      baselinesApi.update(id, body),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: queryKeys.baselines.all });
      qc.invalidateQueries({ queryKey: queryKeys.baselines.detail(vars.id) });
    },
  });
}

export function useDeleteBaseline() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => baselinesApi.delete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.baselines.all });
    },
  });
}

// ---------------------------------------------------------------------------
// Audit
// ---------------------------------------------------------------------------

export function useAuditTimeline(
  entityType: string,
  entityId: string,
  params?: { limit?: number; offset?: number },
) {
  return useQuery({
    queryKey: queryKeys.audit.timeline(entityType, entityId),
    queryFn: () => auditApi.getTimeline(entityType, entityId, params),
    enabled: !!entityType && !!entityId,
  });
}

export function useProjectAudit(projectId: string, params?: ListParams) {
  return useQuery({
    queryKey: queryKeys.audit.project(projectId),
    queryFn: () => auditApi.getProjectAudit(projectId, params),
    enabled: !!projectId,
  });
}

// ---------------------------------------------------------------------------
// Users
// ---------------------------------------------------------------------------

export function useUsers(params?: ListParams) {
  return useQuery({
    queryKey: queryKeys.users.list(params),
    queryFn: () => usersApi.list(params),
  });
}

export function useUser(id: string) {
  return useQuery({
    queryKey: queryKeys.users.detail(id),
    queryFn: () => usersApi.get(id),
    enabled: !!id,
  });
}

export function useInviteUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      email: string;
      display_name?: string;
      role_id: string;
    }) => usersApi.invite(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.users.all });
    },
  });
}

export function useResendInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => usersApi.resendInvite(userId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.users.all });
    },
  });
}

export function useSeatStatus(enabled = true) {
  return useQuery({
    queryKey: [...queryKeys.users.all, "seats"],
    queryFn: () => usersApi.seats(),
    enabled,
    staleTime: 30_000,
  });
}

export function useUpdateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: Partial<User> & { id: string }) =>
      usersApi.update(id, body),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: queryKeys.users.all });
      qc.invalidateQueries({ queryKey: queryKeys.users.detail(vars.id) });
    },
  });
}

export function useDeleteUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => usersApi.delete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.users.all });
    },
  });
}

export function useAssignRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, roleId }: { userId: string; roleId: string }) =>
      usersApi.assignRole(userId, roleId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.users.all });
      qc.invalidateQueries({ queryKey: queryKeys.roles });
    },
  });
}

export function useRemoveRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, roleId }: { userId: string; roleId: string }) =>
      usersApi.removeRole(userId, roleId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.users.all });
      qc.invalidateQueries({ queryKey: queryKeys.roles });
    },
  });
}

// ---------------------------------------------------------------------------
// Roles
// ---------------------------------------------------------------------------

export function useRoles() {
  return useQuery({
    queryKey: queryKeys.roles,
    queryFn: () => rolesApi.list(),
    staleTime: 60_000, // roles change infrequently
  });
}

export function useCreateRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      role_code: string;
      display_name: string;
      description?: string;
      permissions?: string[];
    }) => rolesApi.create(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.roles });
    },
  });
}

// ---------------------------------------------------------------------------
// Status Config
// ---------------------------------------------------------------------------

export function useStatusConfig() {
  return useQuery({
    queryKey: queryKeys.statusConfig,
    queryFn: () => statusConfigApi.list(),
    staleTime: 60_000, // config changes infrequently
  });
}

export function useCreateStatusConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<StatusConfig>) => statusConfigApi.create(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.statusConfig });
    },
  });
}

/** The statuses an entity type can hold. Use this to POPULATE any status
 *  selector rather than hardcoding a list — a hardcoded one drifts from the
 *  registry silently, and the drift shows up as a filter that returns nothing. */
export function useStatusVocabulary(entityType: string) {
  return useQuery({
    queryKey: ["statusVocabulary", entityType] as const,
    queryFn: () => statusConfigApi.registry(entityType),
    staleTime: 5 * 60 * 1000,
    enabled: !!entityType,
  });
}

export function useStatusTransitions(entityType: string, fromStatus: string) {
  return useQuery({
    queryKey: ["statusTransitions", entityType, fromStatus] as const,
    queryFn: () => statusConfigApi.getTransitions(entityType, fromStatus),
    enabled: !!entityType && !!fromStatus,
  });
}

// ---------------------------------------------------------------------------
// Settings
// ---------------------------------------------------------------------------

export function useSettings() {
  return useQuery({
    queryKey: queryKeys.settings,
    queryFn: () => settingsApi.list(),
    staleTime: 60_000,
  });
}

export function useUpdateSetting() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      settingId,
      value,
    }: {
      settingId: string;
      value: unknown;
    }) => settingsApi.update(settingId, value),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.settings });
    },
  });
}

// ---------------------------------------------------------------------------
// Notifications
// ---------------------------------------------------------------------------

export function useNotifications(params?: NotificationListParams) {
  return useQuery({
    queryKey: queryKeys.notifications.list(params),
    queryFn: () => notificationsApi.list(params),
    // Poll notifications more frequently
    refetchInterval: 30_000,
  });
}

export function useMarkNotificationRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => notificationsApi.markRead(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.notifications.all });
    },
  });
}

export function useDismissNotification() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => notificationsApi.dismiss(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.notifications.all });
    },
  });
}

export function useMarkAllNotificationsRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => notificationsApi.markAllRead(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.notifications.all });
    },
  });
}

// ---------------------------------------------------------------------------
// Metrics
// ---------------------------------------------------------------------------

export function useMetrics(params?: ListParams) {
  return useQuery({
    queryKey: queryKeys.metrics.list(params),
    queryFn: () => metricsApi.list(params),
  });
}

export function useMetric(id: string) {
  return useQuery({
    queryKey: queryKeys.metrics.detail(id),
    queryFn: () => metricsApi.get(id),
    enabled: !!id,
  });
}

// ---------------------------------------------------------------------------
// Personas
// ---------------------------------------------------------------------------

export function usePersonas() {
  return useQuery({
    queryKey: queryKeys.personas,
    queryFn: () => personasApi.list(),
    staleTime: 5 * 60_000, // personas change very infrequently
  });
}

// ---------------------------------------------------------------------------
// Health
// ---------------------------------------------------------------------------

export function useHealthAssessment(projectId?: string) {
  return useQuery({
    queryKey: queryKeys.health.latest(projectId),
    queryFn: () => healthApi.getLatest(projectId),
  });
}

export function useAssessHealth() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { project_id: string }) => healthApi.assess(body),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({
        queryKey: queryKeys.health.latest(vars.project_id),
      });
    },
  });
}

// ---------------------------------------------------------------------------
// Workflows
// ---------------------------------------------------------------------------

export function useWorkflowRegistry() {
  return useQuery({
    queryKey: queryKeys.workflows.registry,
    queryFn: () => workflowsApi.getRegistry(),
    staleTime: 5 * 60_000,
  });
}

export function useWorkflowRuns(params?: WorkflowRunListParams) {
  return useQuery({
    queryKey: queryKeys.workflows.runs(params),
    queryFn: () => workflowsApi.listRuns(params),
  });
}

export function useWorkflowRun(runId: string) {
  return useQuery({
    queryKey: queryKeys.workflows.run(runId),
    queryFn: () => workflowsApi.getStatus(runId),
    enabled: !!runId,
    // Active workflow runs should be polled
    refetchInterval: (query) => {
      const data = query.state.data as WorkflowRun | undefined;
      if (data && (data.status === "RUNNING" || data.status === "PAUSED")) {
        return 5_000;
      }
      return false;
    },
  });
}

export function useTriggerWorkflow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: workflowsApi.trigger,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["workflows", "runs"] });
    },
  });
}

export function useResumeWorkflow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      runId,
      body,
    }: {
      runId: string;
      body: { user_input: Record<string, unknown> };
    }) => workflowsApi.resume(runId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["workflows", "runs"] });
    },
  });
}

export function useCancelWorkflow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      runId,
      body,
    }: {
      runId: string;
      body: { reason: string };
    }) => workflowsApi.cancel(runId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["workflows", "runs"] });
    },
  });
}

// ---------------------------------------------------------------------------
// Reports
// ---------------------------------------------------------------------------

export function useReports(params?: ListParams) {
  return useQuery({
    queryKey: queryKeys.reports.list(params),
    queryFn: () => reportsApi.list(params),
  });
}

export function useGenerateReport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      project_id: string;
      report_type: string;
      title?: string;
      parameters?: Record<string, unknown>;
    }) => reportsApi.generate(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.reports.all });
    },
  });
}

// ---------------------------------------------------------------------------
// Billing
// ---------------------------------------------------------------------------

export function useSubscription() {
  return useQuery({
    queryKey: queryKeys.billing.subscription,
    queryFn: () => billingApi.getSubscription(),
    staleTime: 60_000,
  });
}

export function usePlans() {
  return useQuery({
    queryKey: queryKeys.billing.plans,
    queryFn: () => billingApi.getPlans(),
    staleTime: 5 * 60_000,
  });
}

export function useBillingUsage(resource?: string) {
  return useQuery({
    queryKey: queryKeys.billing.usage(resource),
    queryFn: () => billingApi.getUsage(resource),
  });
}

export function useChangePlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (planCode: string) => billingApi.changePlan(planCode),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.billing.subscription });
      qc.invalidateQueries({ queryKey: ["billing", "usage"] });
    },
  });
}

// ---------------------------------------------------------------------------
// Generic status change mutation
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// QA Sessions
// ---------------------------------------------------------------------------

export function useQASessions(params?: QASessionListParams) {
  return useQuery({
    queryKey: queryKeys.qaSessions.list(params),
    queryFn: () => qaSessionsApi.list(params),
    enabled: !!params?.entity_id || !!params?.project_id,
  });
}

export function useQASession(sessionId: string) {
  return useQuery({
    queryKey: queryKeys.qaSessions.detail(sessionId),
    queryFn: () => qaSessionsApi.get(sessionId),
    enabled: !!sessionId,
    refetchInterval: (query) => {
      const data = query.state.data as QASessionDetail | undefined;
      // Keep polling for any non-terminal session status.
      // The backend may set the session to IN_PROGRESS or AWAITING_INPUT
      // while the AI generates the next question in the background.
      const status = data?.session?.status;
      if (status && status !== "COMPLETED" && status !== "FAILED" && status !== "CANCELLED") {
        // Poll faster (1s) while waiting for the first question to be generated
        if (!data.interactions?.length) return 1_000;

        // Poll every 2s when there's no pending question (AI is generating the next one)
        const hasPending = data.interactions.some((i) => !i.answer && i.status === "ACTIVE");
        if (!hasPending) return 2_000;

        // Poll every 3s once we have a pending question (user is typing)
        return 3_000;
      }
      return false;
    },
  });
}

export function useSubmitQAAnswer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ sessionId, answer }: { sessionId: string; answer: string }) =>
      qaSessionsApi.submitAnswer(sessionId, answer),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: queryKeys.qaSessions.detail(vars.sessionId) });
      qc.invalidateQueries({ queryKey: queryKeys.qaSessions.all });
    },
  });
}

export function useEditQAInteraction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      sessionId,
      interactionId,
      answer,
    }: {
      sessionId: string;
      interactionId: string;
      answer: string;
    }) => qaSessionsApi.editInteraction(sessionId, interactionId, answer),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: queryKeys.qaSessions.detail(vars.sessionId) });
    },
  });
}

export function useQAConflicts(sessionId: string, enabled = false) {
  return useQuery({
    queryKey: queryKeys.qaSessions.conflicts(sessionId),
    queryFn: () => qaSessionsApi.getConflicts(sessionId),
    enabled: !!sessionId && enabled,
  });
}

export function useQAImpact(sessionId: string, enabled = false) {
  return useQuery({
    queryKey: queryKeys.qaSessions.impact(sessionId),
    queryFn: () => qaSessionsApi.getImpact(sessionId),
    enabled: !!sessionId && enabled,
  });
}

// ---------------------------------------------------------------------------
// Provenance
// ---------------------------------------------------------------------------

export function useProvenance(entityType: string, entityId: string, params?: {
  include_qa_history?: boolean;
  include_audit_trail?: boolean;
  include_upstream?: boolean;
  include_downstream?: boolean;
}) {
  return useQuery({
    queryKey: queryKeys.provenance.detail(entityType, entityId),
    queryFn: () => provenanceApi.get(entityType, entityId, params),
    enabled: !!entityType && !!entityId,
  });
}

export function useLineageMap(entityType: string, entityId: string) {
  return useQuery({
    queryKey: queryKeys.provenance.lineageMap(entityType, entityId),
    queryFn: () => provenanceApi.getLineageMap(entityType, entityId),
    enabled: !!entityType && !!entityId,
  });
}

// ---------------------------------------------------------------------------
// Traceability Links
// ---------------------------------------------------------------------------

export function useTraceabilityLinks(entityType: string, entityId: string) {
  return useQuery({
    queryKey: queryKeys.traceabilityLinks.detail(entityType, entityId),
    queryFn: () => traceabilityLinksApi.get(entityType, entityId),
    enabled: !!entityType && !!entityId,
  });
}

/** Fetch a graph-format neighborhood (depth-limited) for inline visualization. */
export function useTraceabilityGraph(entityType: string, entityId: string, depth: number = 2) {
  return useQuery({
    queryKey: queryKeys.traceabilityLinks.graph(entityType, entityId),
    queryFn: () => traceabilityLinksApi.getGraph(entityType, entityId, depth),
    enabled: !!entityType && !!entityId,
    staleTime: 60_000,
  });
}

// ---------------------------------------------------------------------------
// Generic status change mutation
// ---------------------------------------------------------------------------

/**
 * Generic hook for changing the status of any entity type.
 * Automatically invalidates the correct query keys based on the entity type.
 *
 * Usage:
 *   const changeStatus = useChangeStatus("projects");
 *   changeStatus.mutate({ id: "...", to_status: "ACTIVE" });
 */
export function useChangeStatus(
  entityType:
    | "projects"
    | "modules"
    | "components"
    | "componentSpecifications"
    | "objectives"
    | "userStories"
    | "userPersonas"
    | "tasks"
    | "testCases"
    | "defects"
    | "documents"
    | "changeRequests"
    | "baselines",
) {
  const qc = useQueryClient();

  const apiMap = {
    projects: projectsApi.changeStatus,
    modules: modulesApi.changeStatus,
    components: componentsApi.changeStatus,
    componentSpecifications: componentSpecificationsApi.changeStatus,
    objectives: objectivesApi.changeStatus,
    userStories: userStoriesApi.changeStatus,
    userPersonas: userPersonasApi.changeStatus,
    tasks: tasksApi.changeStatus,
    testCases: testCasesApi.changeStatus,
    defects: defectsApi.changeStatus,
    documents: documentsApi.changeStatus,
    changeRequests: changeRequestsApi.changeStatus,
    baselines: baselinesApi.changeStatus,
  } as const;

  const changeFn = apiMap[entityType];

  // Optimistic update + rollback. David Demsko 2026-05-02 20:49 ET
  // filed "approval shows error but silently succeeds — status
  // updates after navigation". Root cause: the prior implementation
  // only invalidated queries, so any render error during the
  // refetch + re-render cycle left the user looking at an error
  // toast even though the mutation succeeded. Optimistic update
  // paints the success state on the cached row immediately on
  // mutate; if the server actually rejects, onError rolls back.
  // Settled callback re-invalidates so the row picks up any
  // server-derived fields (timestamps, metadata).
  return useMutation({
    mutationFn: ({
      id,
      ...body
    }: StatusChangeRequest & { id: string }) =>
      changeFn(id, body),
    onMutate: async (vars) => {
      const listKey = queryKeys[entityType].all;
      await qc.cancelQueries({ queryKey: listKey });
      // Snapshot every list query whose key starts with `listKey`
      // so we can rollback on error. React Query keys are arrays;
      // partial-key match means the list-with-filters caches all
      // get touched, which is correct.
      const previous = qc.getQueriesData<
        { items?: Array<{ id: string; status?: string }> } | undefined
      >({ queryKey: listKey });
      qc.setQueriesData<
        { items?: Array<{ id: string; status?: string }> } | undefined
      >({ queryKey: listKey }, (old) => {
        if (!old || !Array.isArray(old.items)) return old;
        return {
          ...old,
          items: old.items.map((it) =>
            it.id === vars.id ? { ...it, status: vars.to_status } : it,
          ),
        };
      });
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      // Roll back every snapshot we took.
      const previous = (ctx as { previous?: Array<[readonly unknown[], unknown]> } | undefined)
        ?.previous;
      if (previous) {
        previous.forEach(([key, data]) => {
          qc.setQueryData(key, data);
        });
      }
    },
    onSettled: (_data, _err, vars) => {
      // Always re-fetch from the server so optimistic state aligns
      // with derived fields (updated_at, agent_rationale, etc.).
      qc.invalidateQueries({ queryKey: queryKeys[entityType].all });
      qc.invalidateQueries({
        queryKey: (queryKeys[entityType] as { detail: (id: string) => readonly unknown[] }).detail(vars.id),
      });
      qc.invalidateQueries({ queryKey: ["health"] });
      // 2026-04-30 entity-UX overhaul: any status flip can change
      // orphan state. Archiving a parent orphans its children;
      // reactivating an archived parent un-orphans them. The plan-
      // builder workflow-progress widget also re-aggregates.
      qc.invalidateQueries({ queryKey: ["orphans"] });
      qc.invalidateQueries({ queryKey: ["workflowProgress"] });
    },
  });
}

// ---------------------------------------------------------------------------
// Conflicts
// ---------------------------------------------------------------------------

export function useProjectConflicts(projectId?: string, params?: ListParams) {
  return useQuery({
    queryKey: queryKeys.conflicts.forProject(projectId ?? ""),
    queryFn: () => conflictsApi.listForProject(projectId!, params),
    enabled: !!projectId,
  });
}

export function useConflict(id?: string) {
  return useQuery({
    queryKey: queryKeys.conflicts.detail(id ?? ""),
    queryFn: () => conflictsApi.get(id!),
    enabled: !!id,
  });
}

export function useEntityConflicts(entityType?: string, entityId?: string) {
  return useQuery({
    queryKey: queryKeys.conflicts.forEntity(entityType ?? "", entityId ?? ""),
    queryFn: () => conflictsApi.listForEntity(entityType!, entityId!),
    enabled: !!entityType && !!entityId,
  });
}

export function useUpdateConflict() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string; status?: string; resolution_notes?: string }) =>
      conflictsApi.update(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.conflicts.all });
    },
  });
}

export function useResolveConflict() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string; resolution_type: string; resolution_notes: string }) =>
      conflictsApi.resolve(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.conflicts.all });
    },
  });
}

export function useDetectConflicts() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ entityType, entityId }: { entityType: string; entityId: string }) =>
      conflictsApi.detect(entityType, entityId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.conflicts.all });
    },
  });
}

// ---------------------------------------------------------------------------
// User Profiles
// ---------------------------------------------------------------------------

export function useMyProfile() {
  return useQuery<UserProfile>({
    queryKey: queryKeys.userProfiles.me,
    queryFn: () => userProfilesApi.getMyProfile(),
  });
}

export function useUserProfile(userId?: string) {
  return useQuery<UserProfile>({
    queryKey: queryKeys.userProfiles.detail(userId ?? ""),
    queryFn: () => userProfilesApi.getProfile(userId!),
    enabled: !!userId,
  });
}

export function useUpdateMyProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<UserProfile>) =>
      userProfilesApi.updateMyProfile(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.userProfiles.me });
      qc.invalidateQueries({ queryKey: queryKeys.userProfiles.team });
    },
  });
}

export function useTeamProfiles() {
  return useQuery({
    queryKey: queryKeys.userProfiles.team,
    queryFn: () => userProfilesApi.listTeam(),
    staleTime: 60_000,
  });
}

// ---------------------------------------------------------------------------
// Meetings
// ---------------------------------------------------------------------------

export function useProjectMeetings(projectId?: string, params?: ListParams) {
  return useQuery({
    queryKey: queryKeys.meetings.forProject(projectId ?? ""),
    queryFn: () => meetingsApi.listForProject(projectId!, params),
    enabled: !!projectId,
  });
}

export function useMeeting(id?: string) {
  return useQuery<Meeting>({
    queryKey: queryKeys.meetings.detail(id ?? ""),
    queryFn: () => meetingsApi.get(id!),
    enabled: !!id,
  });
}

/**
 * Fetch meetings in a date window, merged into the Calendar grid and
 * WorkspaceHub timeline alongside Google Calendar events.
 *
 * Pass `start` / `end` as ISO-8601 strings so query caching stays stable
 * across day boundaries (Dates would break useQuery memoization).
 */
export function useMeetingsByDateRange(params: {
  start?: string;
  end?: string;
  projectId?: string;
  includeCancelled?: boolean;
  enabled?: boolean;
}) {
  const { start, end, projectId, includeCancelled = false, enabled = true } = params;
  return useQuery({
    queryKey: queryKeys.meetings.byDateRange(start ?? "", end ?? "", projectId),
    queryFn: () => meetingsApi.listByDateRange({
      start: start!,
      end: end!,
      project_id: projectId,
      include_cancelled: includeCancelled,
    }),
    enabled: enabled && !!start && !!end,
    staleTime: 30_000,
  });
}

/**
 * useCalendarTimeline — the personal-hub projection. Fetches every
 * date-bearing FTL entity in [start, end] as normalized timeline events.
 * `zoom` is forwarded so the backend can pick a default window if the
 * explicit bounds are ever omitted; we always pass explicit bounds.
 */
export function useCalendarTimeline(params: {
  start: string;
  end: string;
  zoom: "day" | "week" | "month" | "year";
  sources?: string;
  enabled?: boolean;
}) {
  const { start, end, zoom, sources, enabled = true } = params;
  return useQuery({
    queryKey: ["calendar-timeline", start, end, zoom, sources ?? null],
    queryFn: () => calendarApi.timeline({ start, end, zoom, sources }),
    enabled: enabled && !!start && !!end,
    staleTime: 30_000,
  });
}

/**
 * useCalendarComms — the comms-hub slice: pending invites, inbound
 * assignments, and unread notifications in the same window.
 */
export function useCalendarComms(params: {
  start: string;
  end: string;
  zoom: "day" | "week" | "month" | "year";
  enabled?: boolean;
}) {
  const { start, end, zoom, enabled = true } = params;
  return useQuery({
    queryKey: ["calendar-comms", start, end, zoom],
    queryFn: () => calendarApi.comms({ start, end, zoom }),
    enabled: enabled && !!start && !!end,
    staleTime: 30_000,
  });
}

export function useCreateMeeting() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      project_id: string;
      title: string;
      description?: string;
      meeting_type: string;
      platform: string;
      scheduled_start: string;
      scheduled_end?: string;
      agenda?: unknown[];
      participant_ids?: string[];
    }) => meetingsApi.create(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.meetings.all });
    },
  });
}

export function useUpdateMeeting() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string } & Partial<Meeting>) =>
      meetingsApi.update(id, body),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: queryKeys.meetings.all });
      qc.invalidateQueries({ queryKey: queryKeys.meetings.detail(vars.id) });
    },
  });
}

export function useDeleteMeeting() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => meetingsApi.delete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.meetings.all });
    },
  });
}

export function useStartMeeting() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => meetingsApi.start(id),
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: queryKeys.meetings.detail(id) });
      qc.invalidateQueries({ queryKey: queryKeys.meetings.all });
    },
  });
}

export function useEndMeeting() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => meetingsApi.end(id),
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: queryKeys.meetings.detail(id) });
      qc.invalidateQueries({ queryKey: queryKeys.meetings.all });
    },
  });
}

export function useMeetingTranscript(meetingId?: string) {
  return useQuery({
    queryKey: queryKeys.meetings.transcript(meetingId ?? ""),
    queryFn: () => meetingsApi.getTranscript(meetingId!),
    enabled: !!meetingId,
  });
}

export function useMeetingMinutes(meetingId?: string) {
  return useQuery<MeetingMinutes>({
    queryKey: queryKeys.meetings.minutes(meetingId ?? ""),
    queryFn: () => meetingsApi.getMinutes(meetingId!),
    enabled: !!meetingId,
  });
}

export function useMeetingActionItems(meetingId?: string) {
  return useQuery({
    queryKey: queryKeys.meetings.actionItems(meetingId ?? ""),
    queryFn: () => meetingsApi.listActionItems(meetingId!),
    enabled: !!meetingId,
  });
}

export function useCreateActionItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (params: {
      meetingId: string;
      body: {
        title: string;
        description?: string;
        assigned_to: string;
        due_date?: string;
        priority?: string;
      };
    }) => meetingsApi.createActionItem(params.meetingId, params.body),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: queryKeys.meetings.actionItems(vars.meetingId) });
    },
  });
}

export function useUpdateActionItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (params: { meetingId: string; itemId: string; body: Partial<MeetingActionItem> }) =>
      meetingsApi.updateActionItem(params.meetingId, params.itemId, params.body),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: queryKeys.meetings.actionItems(vars.meetingId) });
    },
  });
}

// ---------------------------------------------------------------------------
// Sprints
// ---------------------------------------------------------------------------

export function useProjectSprints(projectId?: string, params?: ListParams) {
  return useQuery({
    queryKey: queryKeys.sprints.forProject(projectId ?? ""),
    queryFn: () => sprintsApi.listForProject(projectId!, params),
    enabled: !!projectId,
  });
}

export function useSprint(id?: string) {
  return useQuery<Sprint>({
    queryKey: queryKeys.sprints.detail(id ?? ""),
    queryFn: () => sprintsApi.get(id!),
    enabled: !!id,
  });
}

export function useCreateSprint() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      project_id: string;
      name: string;
      goal?: string;
      start_date: string;
      end_date: string;
      capacity_points?: number;
    }) => sprintsApi.create(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.sprints.all });
    },
  });
}

export function useUpdateSprint() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string } & Partial<Sprint>) =>
      sprintsApi.update(id, body),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: queryKeys.sprints.all });
      qc.invalidateQueries({ queryKey: queryKeys.sprints.detail(vars.id) });
    },
  });
}

export function useActivateSprint() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => sprintsApi.activate(id),
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: queryKeys.sprints.all });
      qc.invalidateQueries({ queryKey: queryKeys.sprints.detail(id) });
    },
  });
}

export function useCompleteSprint() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => sprintsApi.complete(id),
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: queryKeys.sprints.all });
      qc.invalidateQueries({ queryKey: queryKeys.sprints.detail(id) });
      qc.invalidateQueries({ queryKey: ["sprints", "velocity"] });
    },
  });
}

export function useSprintBacklog(sprintId?: string) {
  return useQuery({
    queryKey: queryKeys.sprints.backlog(sprintId ?? ""),
    queryFn: () => sprintsApi.getBacklog(sprintId!),
    enabled: !!sprintId,
  });
}

export function useAddSprintItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (params: {
      sprintId: string;
      body: {
        entity_type: string;
        entity_id: string;
        story_points?: number;
        assigned_to?: string;
      };
    }) => sprintsApi.addItem(params.sprintId, params.body),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: queryKeys.sprints.backlog(vars.sprintId) });
    },
  });
}

export function useUpdateSprintItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (params: { sprintId: string; itemId: string; body: Partial<SprintItem> }) =>
      sprintsApi.updateItem(params.sprintId, params.itemId, params.body),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: queryKeys.sprints.backlog(vars.sprintId) });
    },
  });
}

export function useRemoveSprintItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (params: { sprintId: string; itemId: string }) =>
      sprintsApi.removeItem(params.sprintId, params.itemId),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: queryKeys.sprints.backlog(vars.sprintId) });
    },
  });
}

export function useSprintBurndown(sprintId?: string) {
  return useQuery({
    queryKey: queryKeys.sprints.burndown(sprintId ?? ""),
    queryFn: () => sprintsApi.getBurndown(sprintId!),
    enabled: !!sprintId,
  });
}

export function useProjectVelocity(projectId?: string) {
  return useQuery({
    queryKey: queryKeys.sprints.velocity(projectId ?? ""),
    queryFn: () => sprintsApi.getVelocity(projectId!),
    enabled: !!projectId,
  });
}

export function useProjectMilestones(projectId?: string) {
  return useQuery({
    queryKey: queryKeys.sprints.milestones(projectId ?? ""),
    queryFn: () => sprintsApi.listMilestones(projectId!),
    enabled: !!projectId,
  });
}

export function useCreateMilestone() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      project_id: string;
      name: string;
      description?: string;
      target_date: string;
      linked_sprint_id?: string;
      criteria?: unknown[];
    }) => sprintsApi.createMilestone(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["sprints", "milestones"] });
    },
  });
}

export function useUpdateMilestone() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (params: { milestoneId: string; body: Partial<Milestone> }) =>
      sprintsApi.updateMilestone(params.milestoneId, params.body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["sprints", "milestones"] });
    },
  });
}

/**
 * Remove a sprint. Row 7def1db9: this had no client at all, and "Create Sprint"
 * is the documented first action a tester takes — mistype it and there was no
 * way back, in the first session. Soft on the server.
 */
export function useDeleteSprint() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (sprintId: string) => sprintsApi.deleteSprint(sprintId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["sprints"] });
    },
  });
}

/** Remove a milestone. Soft on the server, so briefings that name it still
 *  resolve. */
export function useDeleteMilestone() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (milestoneId: string) => sprintsApi.deleteMilestone(milestoneId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["sprints", "milestones"] });
    },
  });
}

// ---------------------------------------------------------------------------
// Requirements Import
// ---------------------------------------------------------------------------

export function useImport(importId?: string) {
  return useQuery<RequirementImport>({
    queryKey: queryKeys.imports.detail(importId ?? ""),
    queryFn: () => importsApi.get(importId!),
    enabled: !!importId,
    refetchInterval: (query) => {
      const status = (query.state.data as RequirementImport | undefined)?.status;
      if (status && !["COMPLETED", "FAILED", "CANCELLED"].includes(status)) {
        return 3_000;
      }
      return false;
    },
  });
}

export function useImportItems(importId?: string, params?: ListParams) {
  return useQuery({
    queryKey: queryKeys.imports.items(importId ?? ""),
    queryFn: () => importsApi.listItems(importId!, params),
    enabled: !!importId,
  });
}

export function useUploadImport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (params: { projectId: string; file: File }) =>
      importsApi.upload(params.projectId, params.file),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.imports.all });
    },
  });
}

export function useReviewImportItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (params: {
      importId: string;
      itemId: string;
      body: {
        status: string;
        user_notes?: string;
        edited_title?: string;
        edited_description?: string;
        edited_category?: string;
        edited_priority?: string;
      };
    }) => importsApi.reviewItem(params.importId, params.itemId, params.body),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: queryKeys.imports.items(vars.importId) });
      qc.invalidateQueries({ queryKey: queryKeys.imports.detail(vars.importId) });
    },
  });
}

export function useAcceptAllImportItems() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (importId: string) => importsApi.acceptAll(importId),
    onSuccess: (_data, importId) => {
      qc.invalidateQueries({ queryKey: queryKeys.imports.items(importId) });
      qc.invalidateQueries({ queryKey: queryKeys.imports.detail(importId) });
    },
  });
}

export function useImportGaps(importId?: string) {
  return useQuery({
    queryKey: queryKeys.imports.gaps(importId ?? ""),
    queryFn: () => importsApi.getGaps(importId!),
    enabled: !!importId,
  });
}

export function useImportConflicts(importId?: string) {
  return useQuery({
    queryKey: queryKeys.imports.conflicts(importId ?? ""),
    queryFn: () => importsApi.getConflicts(importId!),
    enabled: !!importId,
  });
}

// ---------------------------------------------------------------------------
// Document Plans
// ---------------------------------------------------------------------------

export function useDocumentTypes() {
  return useQuery<{ types: DocumentTypeInfo[] }>({
    queryKey: queryKeys.documentPlans.types,
    queryFn: () => documentPlansApi.listTypes(),
    staleTime: 5 * 60_000,
  });
}

export function useProjectDocumentPlan(projectId?: string) {
  return useQuery({
    queryKey: queryKeys.documentPlans.forProject(projectId ?? ""),
    queryFn: () => documentPlansApi.getForProject(projectId!),
    enabled: !!projectId,
  });
}

export function useAssessDocumentRelevance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (projectId: string) => documentPlansApi.assessRelevance(projectId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.documentPlans.all });
    },
  });
}

export function useUpdateDocumentPlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ planId, ...body }: { planId: string; relevance?: string; auto_generate?: boolean }) =>
      documentPlansApi.update(planId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.documentPlans.all });
    },
  });
}

export function useGenerateDocumentFromPlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (planId: string) => documentPlansApi.generate(planId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.documentPlans.all });
      qc.invalidateQueries({ queryKey: queryKeys.documents.all });
    },
  });
}

// ---------------------------------------------------------------------------
// Document Export
// ---------------------------------------------------------------------------

export function useExportDocumentHtml() {
  return useMutation({
    mutationFn: (documentId: string) => documentExportApi.exportHtml(documentId),
  });
}

export function useExportDocumentPdf() {
  return useMutation({
    mutationFn: (documentId: string) => documentExportApi.exportPdf(documentId),
  });
}

export function useExportDocumentDocx() {
  return useMutation({
    mutationFn: (documentId: string) => documentExportApi.exportDocx(documentId),
  });
}

// ---------------------------------------------------------------------------
// Demo Feedback
// ---------------------------------------------------------------------------

export function useDemoCandidates(meetingId?: string, projectId?: string) {
  return useQuery({
    queryKey: queryKeys.demoFeedback.candidates(meetingId ?? ""),
    queryFn: () => demoFeedbackApi.getCandidates(meetingId!, projectId!),
    enabled: !!meetingId && !!projectId,
  });
}

export function useDemoFeedback(meetingId?: string) {
  return useQuery({
    queryKey: queryKeys.demoFeedback.feedback(meetingId ?? ""),
    queryFn: () => demoFeedbackApi.getFeedback(meetingId!),
    enabled: !!meetingId,
  });
}

export function useSubmitDemoFeedback() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (params: {
      meetingId: string;
      body: { feedback_type?: string; content: string; linked_objective_id?: string };
    }) => demoFeedbackApi.submitFeedback(params.meetingId, params.body),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: queryKeys.demoFeedback.feedback(vars.meetingId) });
    },
  });
}

export function useRouteDemoFeedback() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (params: {
      meetingId: string;
      itemId: string;
      body: { route_to: "defect" | "specification" | "clarification" };
    }) => demoFeedbackApi.routeFeedback(params.meetingId, params.itemId, params.body),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: queryKeys.demoFeedback.feedback(vars.meetingId) });
    },
  });
}

// ---------------------------------------------------------------------------
// AD Integration
// ---------------------------------------------------------------------------

export function useAdStatus() {
  return useQuery({
    queryKey: queryKeys.adIntegration.status,
    queryFn: () => adIntegrationApi.getStatus(),
    staleTime: 60_000,
  });
}

export function useAdUsers() {
  return useQuery({
    queryKey: queryKeys.adIntegration.users,
    queryFn: () => adIntegrationApi.getUsers(),
  });
}

export function useAdGroups() {
  return useQuery({
    queryKey: queryKeys.adIntegration.groups,
    queryFn: () => adIntegrationApi.getGroups(),
  });
}

export function useTestAdConnection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => adIntegrationApi.testConnection(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.adIntegration.status });
    },
  });
}

export function useSyncAdUsers() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => adIntegrationApi.syncUsers(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.adIntegration.all });
      qc.invalidateQueries({ queryKey: queryKeys.users.all });
    },
  });
}

export function useImportAdUsers() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { ad_user_ids: string[]; default_role?: string }) =>
      adIntegrationApi.importUsers(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.adIntegration.users });
      qc.invalidateQueries({ queryKey: queryKeys.users.all });
    },
  });
}

export function useUpdateAdGroupMapping() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (params: { groupName: string; body: { ftl_role: string } }) =>
      adIntegrationApi.updateGroupMapping(params.groupName, params.body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.adIntegration.groups });
    },
  });
}

// ---------------------------------------------------------------------------
// User Personas
// ---------------------------------------------------------------------------

export function useUserPersonas(params?: ListParams) {
  return useQuery({
    queryKey: queryKeys.userPersonas.list(params),
    queryFn: () => userPersonasApi.list(params),
  });
}

export function useUserPersona(id: string) {
  return useQuery({
    queryKey: queryKeys.userPersonas.detail(id),
    queryFn: () => userPersonasApi.get(id),
    enabled: !!id,
  });
}

export function useCreateUserPersona() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => userPersonasApi.create(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.userPersonas.all });
    },
  });
}

export function useUpdateUserPersona() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string } & Record<string, unknown>) =>
      userPersonasApi.update(id, body),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: queryKeys.userPersonas.all });
      qc.invalidateQueries({ queryKey: queryKeys.userPersonas.detail(vars.id) });
    },
  });
}

export function useChangeUserPersonaStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string; to_status: string }) =>
      userPersonasApi.changeStatus(id, body),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: queryKeys.userPersonas.all });
      qc.invalidateQueries({ queryKey: queryKeys.userPersonas.detail(vars.id) });
    },
  });
}

export function useUserPersonaHistory(id?: string) {
  return useQuery({
    queryKey: queryKeys.userPersonas.history(id ?? ""),
    queryFn: () => userPersonasApi.getHistory(id!),
    enabled: !!id,
  });
}

export function useDeleteUserPersona() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => userPersonasApi.delete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.userPersonas.all });
    },
  });
}

export function usePersonaStories(id?: string) {
  return useQuery({
    queryKey: queryKeys.userPersonas.stories(id ?? ""),
    queryFn: () => userPersonasApi.getStories(id!),
    enabled: !!id,
  });
}

// ---------------------------------------------------------------------------
// User Stories
// ---------------------------------------------------------------------------

export function useUserStories(params?: ListParams) {
  return useQuery({
    queryKey: queryKeys.userStories.list(params),
    queryFn: () => userStoriesApi.list(params),
  });
}

export function useUserStory(id: string) {
  return useQuery({
    queryKey: queryKeys.userStories.detail(id),
    queryFn: () => userStoriesApi.get(id),
    enabled: !!id,
  });
}

export function useCreateUserStory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => userStoriesApi.create(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.userStories.all });
    },
  });
}

export function useUpdateUserStory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string } & Record<string, unknown>) =>
      userStoriesApi.update(id, body),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: queryKeys.userStories.all });
      qc.invalidateQueries({ queryKey: queryKeys.userStories.detail(vars.id) });
    },
  });
}

export function useChangeUserStoryStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string; to_status: string }) =>
      userStoriesApi.changeStatus(id, body),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: queryKeys.userStories.all });
      qc.invalidateQueries({ queryKey: queryKeys.userStories.detail(vars.id) });
      qc.invalidateQueries({ queryKey: ["health"] });
    },
  });
}

export function useUserStoryHistory(id?: string) {
  return useQuery({
    queryKey: queryKeys.userStories.history(id ?? ""),
    queryFn: () => userStoriesApi.getHistory(id!),
    enabled: !!id,
  });
}

export function useDeleteUserStory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => userStoriesApi.delete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.userStories.all });
    },
  });
}

// ---------------------------------------------------------------------------
// Outcomes
// ---------------------------------------------------------------------------

export function useOutcomes(params?: ListParams) {
  return useQuery({
    queryKey: queryKeys.outcomes.list(params),
    queryFn: () => outcomesApi.list(params),
  });
}

export function useOutcome(id: string) {
  return useQuery({
    queryKey: queryKeys.outcomes.detail(id),
    queryFn: () => outcomesApi.get(id),
    enabled: !!id,
  });
}

export function useCreateOutcome() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => outcomesApi.create(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.outcomes.all });
    },
  });
}

export function useUpdateOutcome() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string } & Record<string, unknown>) =>
      outcomesApi.update(id, body),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: queryKeys.outcomes.all });
      qc.invalidateQueries({ queryKey: queryKeys.outcomes.detail(vars.id) });
    },
  });
}

export function useChangeOutcomeStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string; to_status: string }) =>
      outcomesApi.changeStatus(id, body),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: queryKeys.outcomes.all });
      qc.invalidateQueries({ queryKey: queryKeys.outcomes.detail(vars.id) });
      qc.invalidateQueries({ queryKey: ["health"] });
    },
  });
}

export function useOutcomeHistory(id?: string) {
  return useQuery({
    queryKey: queryKeys.outcomes.history(id ?? ""),
    queryFn: () => outcomesApi.getHistory(id!),
    enabled: !!id,
  });
}

export function useDeleteOutcome() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => outcomesApi.delete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.outcomes.all });
    },
  });
}

// ---------------------------------------------------------------------------
// Felix — AI Program Manager assistant
// ---------------------------------------------------------------------------

export function useFelixChat() {
  return useMutation({
    mutationFn: (body: import("./services").FelixChatRequest) =>
      assistantApi.chat(body),
  });
}

// ---------------------------------------------------------------------------
// Favorites
// ---------------------------------------------------------------------------

export function useProjectFavorites() {
  return useQuery({
    queryKey: ["favorites"],
    queryFn: () => favoritesApi.list(),
    staleTime: 60_000,
  });
}

export function useToggleFavorite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ projectId, favorited }: { projectId: string; favorited: boolean }) =>
      favorited ? favoritesApi.remove(projectId) : favoritesApi.add(projectId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["favorites"] });
    },
  });
}

// ---------------------------------------------------------------------------
// Dashboard Configuration
// ---------------------------------------------------------------------------

export function useDashboardConfig() {
  return useQuery({
    queryKey: ["dashboardConfig"],
    queryFn: () => dashboardApi.getConfig(),
    staleTime: 300_000, // 5 min — rarely changes
  });
}

export function useSaveDashboardConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (config: import("./types").DashboardConfig) =>
      dashboardApi.saveConfig(config),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["dashboardConfig"] });
    },
  });
}

// ---------------------------------------------------------------------------
// Executive Summary
// ---------------------------------------------------------------------------

export function useExecutiveSummary() {
  return useQuery({
    queryKey: ["executiveSummary"],
    queryFn: () => dashboardApi.getExecutiveSummary(),
    staleTime: 120_000, // 2 min
  });
}

// ---------------------------------------------------------------------------
// Q&A Action Items
// ---------------------------------------------------------------------------

export function useMyActionItems(params?: Record<string, string>) {
  return useQuery({
    queryKey: ["qa-action-items", "mine", params],
    queryFn: () => qaActionItemsApi.listMine(params),
  });
}

export function useProjectActionItems(projectId: string, params?: Record<string, string>) {
  return useQuery({
    queryKey: ["qa-action-items", "project", projectId, params],
    queryFn: () => qaActionItemsApi.list({ ...params, project_id: projectId }),
    enabled: !!projectId,
  });
}

export function useActionItem(id: string) {
  return useQuery({
    queryKey: ["qa-action-items", id],
    queryFn: () => qaActionItemsApi.get(id),
    enabled: !!id,
  });
}

export function useActionItemSLA(params?: Record<string, string>) {
  return useQuery({
    queryKey: ["qa-action-items", "sla", params],
    queryFn: () => qaActionItemsApi.slaSummary(params),
  });
}

export function useActionItemRecommend() {
  return useMutation({
    mutationFn: (id: string) => qaActionItemsApi.recommend(id),
  });
}

// ---------------------------------------------------------------------------
// Infrastructure (DigitalOcean)
// ---------------------------------------------------------------------------

export function useInfrastructureSummary() {
  return useQuery({
    queryKey: ["infrastructure", "summary"],
    queryFn: () => infrastructureApi.getSummary(),
    staleTime: 60_000,
    retry: 1,
  });
}

export function useInfrastructureBillingSummary() {
  return useQuery({
    queryKey: ["infrastructure", "billing-summary"],
    queryFn: () => infrastructureApi.getBillingSummary(),
    staleTime: 5 * 60_000,
    retry: 1,
  });
}

/** FTL's hosting bill (one DigitalOcean account for the whole platform), so
 *  the route is platform-admin only. Pass `enabled=false` for anyone else
 *  rather than firing a request that can only come back 403. */
export function useOrgInfrastructureBillingSummary(enabled: boolean = true) {
  return useQuery({
    queryKey: ["org-infrastructure", "billing-summary"],
    queryFn: () => infrastructureApi.getOrgBillingSummary(),
    staleTime: 5 * 60_000,
    retry: 1,
    enabled,
  });
}

export function useInfrastructureInvoices(limit: number = 24) {
  return useQuery({
    queryKey: ["infrastructure", "invoices", limit],
    queryFn: () => infrastructureApi.getInvoices(limit),
    staleTime: 15 * 60_000,
    retry: 1,
  });
}

export function useInfrastructureInvoiceSummary(invoiceUuid: string | undefined) {
  return useQuery({
    queryKey: ["infrastructure", "invoice-summary", invoiceUuid],
    queryFn: () => infrastructureApi.getInvoiceSummary(invoiceUuid!),
    enabled: !!invoiceUuid,
    staleTime: 60 * 60_000,
  });
}

// ---------------------------------------------------------------------------
// Git Configuration — Delivery UX Plan 1.4
// ---------------------------------------------------------------------------

export function useProjectGitConfig(projectId: string | undefined) {
  return useQuery({
    queryKey: ["git-config", projectId],
    queryFn: () => gitConfigApi.get(projectId!),
    enabled: !!projectId,
    staleTime: 30_000,
  });
}

export function useUpsertGitConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ projectId, body }: { projectId: string; body: GitConfigUpdate }) =>
      gitConfigApi.upsert(projectId, body),
    onSuccess: (_, { projectId }) => {
      void qc.invalidateQueries({ queryKey: ["git-config", projectId] });
    },
  });
}

export function useTestGitConnection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ projectId, pat }: { projectId: string; pat?: string }) =>
      gitConfigApi.testConnection(projectId, pat),
    onSuccess: (_, { projectId }) => {
      void qc.invalidateQueries({ queryKey: ["git-config", projectId] });
    },
  });
}

export function useBranchStrategyTemplates(projectId: string | undefined) {
  return useQuery({
    queryKey: ["git-config-templates", projectId],
    queryFn: () => gitConfigApi.getTemplates(projectId!),
    enabled: !!projectId,
    staleTime: 5 * 60_000,
  });
}

// ---------------------------------------------------------------------------
// Felix Ops — row ca2fcad9. Three analytics endpoints and an agent-loop
// monitor that had no screen between them.
// ---------------------------------------------------------------------------

/** Session traces. `mode` filters chat / stream / autonomous. */
export function useFelixTraces(params?: {
  mode?: string;
  had_error?: boolean;
  limit?: number;
}) {
  return useQuery({
    queryKey: ["felix-traces", params],
    queryFn: () => assistantApi.analyticsTraces(params),
    staleTime: 30_000,
  });
}

/** Aggregated quality metrics over a rolling window of hours. */
export function useFelixQuality(hours = 24) {
  return useQuery({
    queryKey: ["felix-quality", hours],
    queryFn: () => assistantApi.analyticsQuality({ hours }),
    staleTime: 60_000,
  });
}

/** Tool failure ranking, past 7 days. */
export function useFelixToolFailures() {
  return useQuery({
    queryKey: ["felix-tool-failures"],
    queryFn: () => assistantApi.analyticsToolFailures(),
    staleTime: 60_000,
  });
}

/** Agent-loop health. Polls, because a stale grade is worse than none. */
export function useAgentLoopHealth() {
  return useQuery({
    queryKey: ["agent-loop-health"],
    queryFn: () => agentLoopApi.health(),
    refetchInterval: 30_000,
    staleTime: 15_000,
  });
}

/** The thresholds behind the grades, so they can be argued with. */
export function useAgentLoopThresholds() {
  return useQuery({
    queryKey: ["agent-loop-thresholds"],
    queryFn: () => agentLoopApi.thresholds(),
    staleTime: 10 * 60_000,
  });
}

/**
 * Remove a traceability link. Row d11b93ec: all 20,347 links were made by an
 * importer or a workflow, and until now there was no way for a person to
 * remove one an importer got wrong — a wrong edge silently distorts the
 * critical path and every projected date downstream.
 *
 * traceabilityLinksApi.delete has existed with no caller; this is the caller.
 */
export function useDeleteTraceabilityLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (linkId: string) => traceabilityLinksApi.delete(linkId),
    onSuccess: () => {
      // The matrix reads the project graph; the per-entity panels read their
      // own key. Both are now stale.
      qc.invalidateQueries({ queryKey: ["traceability-graph"] });
      qc.invalidateQueries({ queryKey: ["traceability-links"] });
    },
  });
}
